// ============================================================
// SAMPLE DATASET — "Try with sample data"
// ============================================================
//
// Produces a realistic retail/e-commerce sales dataset shaped exactly
// like what parseFile() would return from a real CSV/Excel upload
// (an array of plain row objects), so it can flow through the existing
// analyzeData / buildDatasetSchema / buildInsights / buildDashboard /
// createSession pipeline unmodified.
//
// Column names are chosen deliberately to match the keyword heuristics
// in features/data/datasetSchema.js (e.g. "Order Date" -> time column,
// "Revenue"/"Profit"/"Quantity"/"Unit Price" -> measures, "Customer" ->
// person dimension), so dashboards, insights, and natural-language
// questions behave the same as they would for a real uploaded file.

export const SAMPLE_DATASET_FILENAME = "Sample Retail Sales Data.csv";

const CATEGORIES = {
  Electronics: {
    products: ["Wireless Headphones", "4K Monitor", "Bluetooth Speaker", "Laptop Stand", "USB-C Hub", "Smartwatch"],
    priceRange: [39, 899],
    marginRange: [0.12, 0.32],
  },
  Furniture: {
    products: ["Ergonomic Office Chair", "Standing Desk", "Bookshelf", "Filing Cabinet", "Conference Table"],
    priceRange: [89, 1299],
    marginRange: [0.18, 0.4],
  },
  "Office Supplies": {
    products: ["Printer Paper (Case)", "Ink Cartridge Set", "Desk Organizer", "Whiteboard", "Sticky Notes (Bulk)"],
    priceRange: [6, 180],
    marginRange: [0.2, 0.45],
  },
  Apparel: {
    products: ["Cotton T-Shirt", "Fleece Hoodie", "Running Shoes", "Denim Jacket", "Wool Sweater"],
    priceRange: [14, 220],
    marginRange: [0.25, 0.5],
  },
  "Home & Garden": {
    products: ["Cordless Vacuum", "Air Purifier", "Garden Hose Set", "LED Desk Lamp", "Ceramic Planter"],
    priceRange: [12, 450],
    marginRange: [0.15, 0.38],
  },
};

const REGIONS = ["North America", "Europe", "Asia Pacific", "Latin America"];

const CUSTOMERS = [
  "Acme Retail Group", "Blue Harbor Traders", "Summit Office Partners", "Northwind Commerce",
  "Cascade Home Goods", "Pinnacle Distributors", "Metro Supply Co.", "Evergreen Wholesale",
  "Harbor Point Retail", "Crestline Enterprises", "Union Square Traders", "Redwood Logistics",
  "Silver Peak Markets", "Lakeside Merchants", "Vantage Point Retail", "Brightside Commerce",
];

// Small deterministic PRNG (mulberry32) so the sample dataset is stable
// and reproducible across page loads rather than regenerating randomly
// on every visit.
function createRng(seed) {
  let a = seed;
  return function rng() {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pick(rng, arr) {
  return arr[Math.floor(rng() * arr.length)];
}

function randomInRange(rng, min, max) {
  return min + rng() * (max - min);
}

function formatDate(date) {
  return date.toISOString().slice(0, 10); // YYYY-MM-DD
}

// Generates ~`rowCount` rows spread across the trailing 12 months so the
// dataset always looks current relative to whenever it's loaded.
export function generateSampleDataset(rowCount = 650, seed = 20260724) {
  const rng = createRng(seed);
  const categoryNames = Object.keys(CATEGORIES);

  const end = new Date();
  const start = new Date(end);
  start.setMonth(start.getMonth() - 11);
  start.setDate(1);

  const spanMs = end.getTime() - start.getTime();

  const rows = [];

  for (let i = 0; i < rowCount; i++) {
    const category = pick(rng, categoryNames);
    const { products, priceRange, marginRange } = CATEGORIES[category];
    const product = pick(rng, products);

    const orderDate = new Date(start.getTime() + rng() * spanMs);

    const quantity = Math.max(1, Math.round(randomInRange(rng, 1, 18)));
    const unitPrice = Math.round(randomInRange(rng, priceRange[0], priceRange[1]) * 100) / 100;
    const revenue = Math.round(quantity * unitPrice * 100) / 100;

    // Occasional underperforming/loss-making orders (returns, heavy
    // discounting) so "worst-performing product" questions have a real
    // answer instead of everything looking uniformly profitable.
    const isUnderperformer = rng() < 0.06;
    const margin = isUnderperformer
      ? randomInRange(rng, -0.15, 0.05)
      : randomInRange(rng, marginRange[0], marginRange[1]);

    const profit = Math.round(revenue * margin * 100) / 100;

    rows.push({
      "Order Date": formatDate(orderDate),
      "Product": product,
      "Category": category,
      "Region": pick(rng, REGIONS),
      "Customer": pick(rng, CUSTOMERS),
      "Quantity": quantity,
      "Unit Price": unitPrice,
      "Revenue": revenue,
      "Profit": profit,
    });
  }

  // Chronological order, like a real sales export would be.
  rows.sort((a, b) => new Date(a["Order Date"]) - new Date(b["Order Date"]));

  return rows;
}
